import '@usb-shield/react-modal/dist/library/styles/index.css'
import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import {useSession} from 'next-auth/react'
import USBLink from '@usb-shield/react-link'
import {downloadDocument} from '@/modules/products/ProductOverviewPage/services/downloadDocument.service'
import styles from '@/modules/products/ProductDocumentPage/DisplayViews/displayViews.module.scss'
import {useCookies} from 'react-cookie'
import {NavItemProps} from '@/modules/products/ProductDocumentPage/components/SideNavMenu/SideNavMenu.model'
import {USBIconExternalLink} from '@usb-shield/react-icons'

/**
 * Redirects to the hash value on the first page load.
 * @param itemId - The ID of the item to redirect to.
 */
export function handleInitialHashRedirection(
  itemId: string,
  path: string,
  menuLevel: number,
  router: ReturnType<typeof useRouter>,
  resourcePath: number,
  segments: string[]
): void {
  const hashValFromUrl = path?.split('#')[1]
  if (segments[0] != 'dashboard' && menuLevel >= resourcePath && (!hashValFromUrl || hashValFromUrl === '') && process.env.SITE_BRAND != 'elavon') {
    const newUrl = `${path}#${itemId}`
    // Use router.push to update the URL
    router?.push(newUrl)?.then(() => {
      // Wait for the page to update and then scroll to the element
      const element = document.getElementById(itemId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    })
  }
}

/**
 * Generates a link with a hash value if applicable.
 * @param linkUrl - The base URL of the link.
 * @param menuLevel - The menu level of the item.
 * @param itemId - The ID of the item.
 * @returns The full URL with the hash value if conditions are met.
 */
export function generateLinkWithHash(linkUrl: string | undefined, menuLevel: number | undefined, itemId: string, resourcePath: number, resourseBasePath: any[]): string {
  if (!linkUrl) {
    return ''
  }

  if (resourseBasePath[1] != 'dashboard' && menuLevel && menuLevel >= resourcePath && process.env.SITE_BRAND != 'elavon') {
    return `${linkUrl}#${itemId}`
  }

  return linkUrl
}

const DocumentLinkProcessor = ({
  item,
  hasSubNav,
  isShown,
}: {
  item: NavItemProps<string>
  hasSubNav: boolean | undefined
  isShown: string | undefined
}) => {
  const { status } = useSession()
  const [cookies] = useCookies(['locale'])
  const [redirectToHash, setRedirectToHash] = useState(false)

  const title = item.title

  const router = useRouter()
  const menuLevel = item?.id?.split('-').length

  let resourseBasePath = item.path?.split('/') || ''

  let segments = router.asPath.replace(/^\/+/,'').split('/')
  let resourcePath: any;
  if (segments?.[0] === 'products') {
      // this is a resource guide pages or product documentation pages
      resourcePath = 3
  } else {
      resourcePath = 2
  }

  useEffect(() => {
    // Handle initial hash redirection when the component mounts
    // This will only run on the client side
    // and will redirect to the hash value if it exists in the URL
    if(resourseBasePath[1] != 'dashboard' && menuLevel >= resourcePath && router.asPath == item.path) {
      // Redirect to the hash value if it exists in the URL
      handleInitialHashRedirection(item.id, item.path, menuLevel, router, resourcePath, segments)
    }
  }, [item.id, item.path, menuLevel, router, redirectToHash])

  const getDocumentationLinks = (value: any) => {
    let isElavonApiNewTab = false
    let linkUrl = value.path
    
    if (
        process.env.CONTENT_BRAND === 'elavon' &&
        (value.metaData.fileType === 'renderAsApiSpec' || value.metaData.newTab == 'true')
    ) {
      isElavonApiNewTab = true
    }
    
    if (
        process.env.CONTENT_BRAND === 'elavon' &&
        value.metaData.externalURL !== undefined &&
        value.metaData.externalURL !== null &&
        value.metaData.externalURL !== ''
    ) {
      linkUrl = value.metaData.externalURL
    }

    const ariaProps: { [index: string]: any } = {}

    ariaProps['label'] = hasSubNav
      ? 'Expandable control for ' + item.title + '. Menu Level ' + menuLevel
      : item.title + '. Menu Level ' + menuLevel

    if (hasSubNav) {
      ariaProps['expanded'] = isShown?.startsWith(item.id)
    }

    if (value?.metaData?.fileType === 'download') {
      return (
        <USBLink
          linkType="link"
          handleClick={async () => {
            const documentPath = value.path.replace('/', '')
            const path = documentPath.split("/");
            if(path.length === 5)
            path.splice(1, 1); // Remove the element at index 1
            const resultPath = path.join("/");
            await downloadDocument(resultPath, value.title, cookies.locale)
          }}
        >
          {title}
        </USBLink>
      )
    }

    return (
      <>
      {item?.iconImage && (
        <img src={item?.iconImage} alt={item.title} className={styles.iconImage} />
      )}
      <USBLink
        linkType="link"
        href={generateLinkWithHash(linkUrl, menuLevel, item.id, resourcePath, resourseBasePath)}
        target={isElavonApiNewTab ? '_blank' : '_self'}
        addClasses={
          isShown === item.id
            ? `${styles.productDocument} ${styles.selectedTocTitle} ${styles.centerLinkIcon}`
            : `${styles.productDocument} ${styles.centerLinkIcon}`
        }
        ariaProps={ariaProps}
        role="button"
        handleClick={() => {
          // Check if the menu level is 3 or higher before handling the redirection
          if (menuLevel >= resourcePath) {
            setRedirectToHash(true)
          }
        }}
      >
        {title}

        {isElavonApiNewTab && (
          <USBIconExternalLink colorVariant="grey80" size={20} />
        )}
      </USBLink>
      </>
    )
  }

  if (status !== 'loading') {
    return <>{getDocumentationLinks(item)}</>
  }

  return <></>
}

export default DocumentLinkProcessor